import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { toastEventDispatch } from '@hotmart/space-lib-events/dist/toast'
import { ToastType, ToastPosition } from '@hotmart/space-lib-events/dist/toast/enum'

import { useModal } from 'src/hook/useModal'
import i18n from 'src/i18n'
import { useSchedulePostForm } from 'src/hook/useSchedulePostForm'
import { DateUtils } from 'src/utils/date'
import { DayjsUtils } from 'src/utils/dayjs'
import { useGetInfinityScheduledPosts } from 'src/hook/useGetScheduledPosts'
import { AVATAR_SIZE } from 'src/utils/constants'

import { useCommunityDetails } from '../Posts/context/communityDetailsContext'
import SchedulePostModalList from './schedulePostModalList'
import SchedulePostModalForm from './schedulePostModalForm'

const publishingError = {
  type: ToastType.DANGER,
  message: i18n.t('toasts.error_in_publish'),
  position: ToastPosition.BOTTOM_RIGHT
}

export const useSchedulePostsRequest = () => {
  const { idCommunity } = useParams()
  const { community } = useCommunityDetails()

  const infinityReq = useGetInfinityScheduledPosts({
    avatarSize: AVATAR_SIZE,
    id: Number(idCommunity) || Number(community?.id)
  })

  const hasScheduledPosts = useMemo(
    () => infinityReq.data?.pages.some((page) => Number(page.data.length) > 0),
    [infinityReq.data]
  )

  return { infinityReq, hasScheduledPosts }
}

export const SchedulePostModal = () => {
  const formId = 'schedule-posts-form'
  const { closeModal, setModalContent } = useModal()

  const {
    formIsInvalid,
    formData,
    setFormData,
    resetForm,
    isScheduleFormSaved,
    setIsScheduleFormSaved
  } = useSchedulePostForm()
  const {
    hasScheduledPosts,
    infinityReq: { isLoading: isLoadingScheduledPosts, isError: scheduledPostsReqHasError }
  } = useSchedulePostsRequest()

  const handleSubmit = () => {
    const date = DateUtils.convertStringToISODate(formData?.scheduledPubDate ?? '')

    if (!date || !formData.scheduledPubTime) {
      return toastEventDispatch(publishingError)
    }

    const dateObj = {
      date,
      time: formData.scheduledPubTime,
      tz: formData.scheduledPubTimeZoneId
    }

    const ISOdate = DayjsUtils.convertDateTimeToTz(dateObj).utc().format()

    if (!ISOdate) {
      return toastEventDispatch(publishingError)
    }

    setFormData((prev) => ({ ...prev, pubDate: ISOdate }))

    setIsScheduleFormSaved(true)
    closeModal()
  }

  const handleGoBack = () => {
    !formData?.pubDate && resetForm()
    closeModal()
  }

  const handleClear = () => {
    resetForm()
    closeModal()
  }

  const handleShowList = () =>
    setModalContent(
      <SchedulePostModalList
        formId={formId}
        formIsInvalid={formIsInvalid}
        isLoadingScheduledPosts={isLoadingScheduledPosts}
        scheduledPostsReqHasError={scheduledPostsReqHasError}
        isScheduleFormSaved={isScheduleFormSaved}
        handleGoBack={handleGoBack}
        handleShowList={handleShowList}
        handleClear={handleClear}
        handleSubmit={handleSubmit}
        hasScheduledPosts={hasScheduledPosts}
      />
    )

  return (
    <SchedulePostModalForm
      formId={formId}
      formIsInvalid={formIsInvalid}
      isLoadingScheduledPosts={isLoadingScheduledPosts}
      scheduledPostsReqHasError={scheduledPostsReqHasError}
      isScheduleFormSaved={isScheduleFormSaved}
      handleGoBack={handleGoBack}
      handleShowList={handleShowList}
      handleClear={handleClear}
      handleSubmit={handleSubmit}
      hasScheduledPosts={hasScheduledPosts}
    />
  )
}
